import { BaseModel, HasId } from '@app/shared/models/base';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import * as _ from 'lodash-es';
import { TranslationEntry } from '@app/shared/models/translatable';
import { AlphabetFilterable } from '@app/shared/models/search-filter';

export enum CredentialsType {
    EMAIL = 'email'
}

export enum CredentialsAssignment {
    BASIC = 'basic',
    OFFICES = 'offices',
    USERS = 'users'
}

export class CredentialsAssignments {

    public static readonly ALL: TranslationEntry[] = [
        { key: CredentialsAssignment.BASIC, trans: marker('credentials.assignment_type.basic') },
        { key: CredentialsAssignment.OFFICES, trans: marker('credentials.assignment_type.offices') },
        { key: CredentialsAssignment.USERS, trans: marker('credentials.assignment_type.users') }
    ];

    public static getByKey(key: string): TranslationEntry {
        key = typeof key === 'string' ? key.toLowerCase() : key;
        return _.find(CredentialsAssignments.ALL, (type: TranslationEntry) => type.key === key);
    }

    public static get BASIC() {
        return CredentialsAssignments.getByKey(CredentialsAssignment.BASIC);
    }

    public static get OFFICES() {
        return CredentialsAssignments.getByKey(CredentialsAssignment.OFFICES);
    }

    public static get USERS() {
        return CredentialsAssignments.getByKey(CredentialsAssignment.USERS);
    }

}

export enum EmailSecurityOptions {
    SSL = 'SSL',
    STARTTLS = 'STARTTLS',
    TLS = 'TLS'
}

export class Credentials extends BaseModel
                         implements HasId {

    get id(): CredentialsType {
        return this.credentialsType;
    }

    public credentialsType: CredentialsType;

    public advanced: boolean;
    public assignment: CredentialsAssignment;
    public basic: BasicCredentials;
    public groups: CredentialGroupsContainer;

    constructor(json: any) {
        super(json);
    }

    public static generateCredentialsBasedOnType(json: any, type: CredentialsType, assignment?: CredentialsAssignment): BasicCredentials | AdvancedCredentials {
        switch (type) {
            case CredentialsType.EMAIL:
                if (assignment && assignment !== CredentialsAssignment.BASIC) {
                    json.assignment = assignment;
                    return new AdvancedEmailCredentials(json);
                } else {
                    return new EmailCredentials(json);
                }
            default:
                return null;
        }
    }

    protected setObjectMappings(json: any): any {
        const cloned = _.cloneDeep(json);
        if (cloned && cloned.groups) {
            if (cloned.groups.users) {
                const advancedUserCredentials = cloned.groups.users && cloned.groups.users.credentials ?
                                                cloned.groups.users.credentials.map(advancedCred => Credentials.generateCredentialsBasedOnType(advancedCred, cloned.credentialsType, CredentialsAssignment.USERS)) :
                                                null;
                cloned.groups.users.credentials = advancedUserCredentials;
            }

            if (json.groups.offices) {
                const advancedOfficeCredentials = cloned.groups.offices && cloned.groups.offices.credentials ?
                                                  cloned.groups.offices.credentials.map(advancedCred => Credentials.generateCredentialsBasedOnType(advancedCred, cloned.credentialsType, CredentialsAssignment.OFFICES)) :
                                                  null;
                cloned.groups.offices.credentials = advancedOfficeCredentials;
            }
        }

        const mappedObject = Object.assign(super.setObjectMappings(cloned), {
            basic: cloned && cloned.basic ? Credentials.generateCredentialsBasedOnType(cloned.basic, cloned.credentialsType, CredentialsAssignment.BASIC) : null,
            groups: cloned && cloned.groups ? new CredentialGroupsContainer(cloned.groups) : null
        });

        return mappedObject;
    }

    public mergeWith(result: Credentials): void {
        this.advanced = result.advanced;
        this.assignment = result.assignment;
        BasicCredentials.mergeCredentials(this.basic, result.basic);
        this.groups = new CredentialGroupsContainer(result.groups);
    }

    public getInUpdateFormat(): Credentials {
        const copy = new Credentials(this);
        delete copy.groups;
        delete copy.credentialsType;
        return copy;
    }
}

export abstract class BasicCredentials extends BaseModel {
    public static isAdvancedCredentials(cred: any): cred is AdvancedCredentials {
        return 'id' in cred &&
               'name' in cred &&
               'assignment' in cred;
    }

    public static mergeCredentials<T extends BasicCredentials>(into: T, from: T): void {
        if (into instanceof EmailCredentials && from instanceof EmailCredentials) {
            into.email = from.email;
            into.provider = from.provider;
            into.host = from.host;
            into.security = from.security;
            into.port = from.port;
            into.user = from.user;
            into.password = from.password;
        }
    }

    public abstract credentialsComplete(): boolean;
}

export interface AdvancedCredentials extends HasId, AlphabetFilterable {
    name: string;
    assignment: CredentialsAssignment;
    credentialsComplete(): boolean;
}

export class EmailCredentials extends BasicCredentials {
    public email: string;
    public host: string;
    public password: string;
    public port: number;
    public provider: string;
    public security: EmailSecurityOptions;
    public user: string;
    public client: string;
    public key: string;

    public readonly trans = {
        email: marker('credentials.email'),
        host: marker('credentials.host'),
        password: marker('credentials.password'),
        port: marker('credentials.port'),
        provider: marker('credentials.provider'),
        security: marker('credentials.security'),
        user: marker('credentials.user')
    };

    constructor(json?: any) {
        super(json);
    }

    public credentialsComplete(): boolean {
        // this.provider is not included because the input is optional
        return !!(this.email && this.host && this.port && this.security && this.user && this.password);
    }
}

export class AdvancedEmailCredentials extends EmailCredentials
                                      implements AdvancedCredentials {
    public id: string;
    public name: string;
    public assignment: CredentialsAssignment;

    get alphabetFilterOn() {
        return [this.name];
    }

    constructor(json?: any) {
        super(json);
    }

    public readonly trans = Object.assign(this.trans, {
        name: marker('credentials.name')
    });
}

export class CredentialGroupsContainer extends BaseModel {
    public users: CredentialGroup;
    public offices: CredentialGroup;

    constructor(json?: any) {
        super(json);
    }

    protected setObjectMappings(json: any): any {
        return Object.assign(super.setObjectMappings(json), {
            users: json && json.users ? new CredentialGroup(json.users) : null,
            offices: json && json.offices ? new CredentialGroup(json.offices) : null
        });
    }
}

export class CredentialGroup extends BaseModel {
    public credentials: AdvancedCredentials[];
    public from: number;
    public size: number;
    public total: number;

    constructor(json?: any) {
        super(json);
    }

    public getCredentialsById(id: string): AdvancedCredentials {
        return this.credentials.find(cred => cred.id === id);
    }

    public setCredentialsById(cred: AdvancedCredentials): void {
        for (let i = 0; i < this.credentials.length; i++) {
            if (this.credentials[i].id === cred.id) {
                this.credentials[i] = cred;
            }
        }
    }

    public replaceCredentialsValues(cred: AdvancedCredentials): void {
        for (let i = 0; i < this.credentials.length; i++) {
            if (this.credentials[i].id === cred.id) {
                if (cred instanceof EmailCredentials) {
                    const into = this.credentials[i] as AdvancedEmailCredentials;
                    BasicCredentials.mergeCredentials(into, cred);
                }
            }
        }
    }
}
